import React, { createContext, useEffect, useRef, useState } from "react";
import api, { apiAuth } from "../utils/api";
import { jwtDecode } from "jwt-decode";
import {feedCacheKey} from "../utils/feedSnapshot";

export const AuthContext = createContext();

const LOGOUT_FLAG_KEY = "access_token_logged_out";
const USER_AVATAR_KEY = "user_avatar_url";

function getUsableStoredAccessToken() {
  const token = localStorage.getItem("access_token");
  if (!token || token === "null" || token === "undefined") return null;

  try {
    const decoded = jwtDecode(token);
    if (decoded.exp && decoded.exp * 1000 <= Date.now() + 30000) {
      localStorage.removeItem("access_token");
      return null;
    }
    return token;
  } catch {
    localStorage.removeItem("access_token");
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(() =>
    getUsableStoredAccessToken()
  );

  const [user, setUser] = useState(() => {
    const t = getUsableStoredAccessToken();
    const cachedAvatar = localStorage.getItem(USER_AVATAR_KEY);
    try {
      return t
        ? {
            ...jwtDecode(t),
            user_uuid: jwtDecode(t).user_uuid || jwtDecode(t).sub,
            avatar_url: cachedAvatar || null, // Hydrate image cleanly on initialization
          }
        : null;
    } catch {
      return null;
    }
  });

  const updateUserData = (newData) => {
    setUser((prev) => {
      if (!prev) return null;
      const merged = { ...prev, ...newData };
      const avatarUrl = merged.avatar_url || merged.profile_image_url || null;
      if (avatarUrl) {
        localStorage.setItem(USER_AVATAR_KEY, avatarUrl);
      } else {
        localStorage.removeItem(USER_AVATAR_KEY);
      }
      return merged;
    });
  };

  const hydrateUserProfile = async () => {
    try {
      const { data } = await api.get("/me");
      if (!data) return null;
      updateUserData({
        ...data,
        user_uuid: data.user_uuid,
        avatar_url: data.avatar_url || null,
        profile_image_url: data.avatar_url || null,
      });
      return data;
    } catch (err) {
      console.error("Failed to hydrate user profile:", err);
      return null;
    }
  };

  const refreshInFlight = useRef(null);
  const isLoggedOut = useRef(
    localStorage.getItem(LOGOUT_FLAG_KEY) === "1"
  );

  const setToken = (token, extraData = null) => {
    setAccessToken(token);
    localStorage.setItem("access_token", token);

    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    apiAuth.defaults.headers.common.Authorization = `Bearer ${token}`;

    try {
      const decoded = jwtDecode(token);
      
      // Pull avatar from payload parameter or fallback to memory
      const avatarUrl = extraData?.avatar_url || extraData?.profile_image_url || localStorage.getItem(USER_AVATAR_KEY) || null;

      setUser({
        ...decoded,
        user_uuid: decoded.user_uuid || decoded.sub,
        avatar_url: avatarUrl,
      });

      if (avatarUrl) {
        localStorage.setItem(USER_AVATAR_KEY, avatarUrl);
      }
    } catch {
      setUser(null);
    }
  };

  const clearToken = () => {
    try { if(user?.user_uuid)localStorage.removeItem(feedCacheKey(user.user_uuid)); } catch {}
    setAccessToken(null);
    setUser(null);
    localStorage.removeItem("access_token");
    localStorage.removeItem(USER_AVATAR_KEY); // Remove avatar caching on logout

    delete api.defaults.headers.common.Authorization;
    delete apiAuth.defaults.headers.common.Authorization;
  };

  const login = async (identifier, password) => {
    const res = await apiAuth.post(
      "/auth/login",
      { identifier, password },
      { withCredentials: true }
    );

    console.log("RAW BACKEND LOGIN RESPONSE DATA:", res.data);

    const token = res.data?.access_token || res.data?.token;
    if (!token) throw new Error("No access token returned from login");

    console.log("DECODED JWT CLAIMS:", jwtDecode(token));

    isLoggedOut.current = false;
    localStorage.removeItem(LOGOUT_FLAG_KEY);

    // Pass the raw body response payload down into setToken to extract the image
    setToken(token, res.data);
    await hydrateUserProfile();
    return true;
  };

  const logout = async () => {
    isLoggedOut.current = true;
    localStorage.setItem(LOGOUT_FLAG_KEY, "1");

    try {
      await apiAuth.post("/auth/logout", {}, { withCredentials: true });
    } catch (_) {}

    clearToken();
  };

  const refresh = async () => {
    if (isLoggedOut.current || localStorage.getItem(LOGOUT_FLAG_KEY) === "1") {
      clearToken();
      return null;
    }

    if (refreshInFlight.current) return refreshInFlight.current;

    refreshInFlight.current = (async () => {
      try {
        const res = await apiAuth.post(
          "/auth/refresh",
          {},
          { withCredentials: true }
        );

        const newTok = res.data?.access_token || res.data?.token;
        if (!newTok) throw new Error("No access token returned from refresh");

        // Pass refresh data if it carries updated avatar properties
        setToken(newTok, res.data);
        await hydrateUserProfile();
        return newTok;
      } catch (e) {
        isLoggedOut.current = true;
        localStorage.setItem(LOGOUT_FLAG_KEY, "1");
        clearToken();
        return null;
      } finally {
        refreshInFlight.current = null;
      }
    })();

    return refreshInFlight.current;
  };

  useEffect(() => {
    const attach = (instance) => {
      const reqId = instance.interceptors.request.use((config) => {
        const tok = getUsableStoredAccessToken();
        config.headers = config.headers || {};
        if (tok) {
          config.headers.Authorization = `Bearer ${tok}`;
        } else if (config.headers.Authorization) {
          delete config.headers.Authorization;
        }
        return config;
      });

      const resId = instance.interceptors.response.use(
        (res) => res,
        async (err) => {
          const original = err.config || {};
          const status = err.response?.status;

          const urlStr = (original.url || "").toString();
          const isAuthEndpoint =
            urlStr.includes("/auth/login") ||
            urlStr.includes("/auth/refresh") ||
            urlStr.includes("/auth/logout");

          if (!original || original._retry || isAuthEndpoint || status !== 401) {
            return Promise.reject(err);
          }

          if (isLoggedOut.current || localStorage.getItem(LOGOUT_FLAG_KEY) === "1") {
            clearToken();
            return Promise.reject(err);
          }

          original._retry = true;

          const newTok = await refresh();
          if (!newTok) return Promise.reject(err);

          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newTok}`;
          return instance(original);
        }
      );

      return { reqId, resId };
    };

    const a = attach(api);
    const b = attach(apiAuth);

    return () => {
      api.interceptors.request.eject(a.reqId);
      api.interceptors.response.eject(a.resId);
      apiAuth.interceptors.request.eject(b.reqId);
      apiAuth.interceptors.response.eject(b.resId);
    };
  }, []);

  useEffect(() => {
    (async () => {
      const loggedOutFlag = localStorage.getItem(LOGOUT_FLAG_KEY) === "1";

      if (accessToken && !loggedOutFlag && !isLoggedOut.current) {
        await hydrateUserProfile();
        return;
      }

      if (!accessToken && !loggedOutFlag && !isLoggedOut.current) {
        try {
          await refresh();
        } catch (_) {}
      }
    })();
  }, [accessToken]);

  const isAuthenticated = !!accessToken;

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isAuthenticated,
        updateUserData,
        refresh,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
